import SurveyTree from './SurveyTree';
import SurveyItem, { SurveyItemTypes } from './SurveyItem';
import isArray from 'lodash/isArray';
import flatten from 'lodash/flatten';
import { MessageDescriptor } from 'react-intl';
import { questionTypeMessageDescriptors } from './SurveyBuilderQuestionTypes';
import truncate from 'lodash/truncate';

export type QuestionSearchItem = {
  fullDisplay: string;
  parentDisplay: string;
  itemDisplay: string;
  linkId: string;
  searchParts: string[];
  itemDescriptor: MessageDescriptor;
  surveyId: string;
  surveyItem: SurveyItem;
};

/**
 * An individual item for search and its source SurveyItem
 */
export class SearchItem {
  static readonly searchPartSeperator = ' > ';
  private _surveyItem: SurveyItem;
  private _surveyId: string;
  private _parentTextParts: string[];

  constructor(
    surveyItem: SurveyItem,
    parentTextParts: string[] = [],
    surveyId?: string,
  ) {
    // TODO: make surveyId required after primary survey refactor
    // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
    this._surveyId = surveyId;
    this._surveyItem = surveyItem;
    this._parentTextParts = parentTextParts;
  }

  /**
   * Retrieves the children search items and this search item in a combined array.
   */
  getTargetAndChildSearchItems = (): SearchItem[] => {
    const childrenSurveyItems = this.surveyItem.subItems;

    if (
      !childrenSurveyItems ||
      !isArray(childrenSurveyItems) ||
      childrenSurveyItems.length === 0
    ) {
      return [this];
    } else {
      const childSearchItems = this._surveyItem.subItems.map(subItem => {
        const searchItem = new SearchItem(subItem, this.textParts);
        return searchItem.getTargetAndChildSearchItems();
      });

      return [this, ...flatten(childSearchItems)];
    }
  };

  get surveyItem() {
    return this._surveyItem;
  }

  get itemDescription(): MessageDescriptor {
    const itemType = this._surveyItem.surveyBuilderQuestionType;

    // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
    if (!itemType) return null;

    return questionTypeMessageDescriptors[itemType] || null;
  }

  /**
   * Gets the text search parts.  If this is not a valid search item, it will only return parent text search parts
   */
  get textParts(): string[] {
    if (!this.isValid) return this._parentTextParts;

    return [...this._parentTextParts, this._surveyItem.text];
  }

  /**
   * The text of all combined search parts, returns null if not a valid search item
   */
  get searchText() {
    if (!this.isValid) return null;

    return this.textParts.join(SearchItem.searchPartSeperator);
  }

  /**
   * The text of all combined parent parts.  Useful for determining what page & group an item is apart of
   */
  get pathToSearchText(): string {
    return this._parentTextParts.join(SearchItem.searchPartSeperator);
  }

  get itemText(): string {
    return this._surveyItem.text;
  }

  /**
   * Determines if search text really applies to this survey item
   */
  get isValid() {
    if (!this._surveyItem) return false; // should never happen, just to be safe

    if (this._surveyItem.isSurveyPageGroup || this._surveyItem.isControl) {
      return true;
    }
  }

  /**
   * Is the target survey item a control
   */
  get isSearchTextForControl() {
    if (!this.isValid) return false;

    return this._surveyItem.isControl;
  }

  /**
   * The link id of the target item
   */
  get itemLinkId() {
    return this._surveyItem.link;
  }

  get surveyId() {
    return this._surveyId;
  }
  /**
   * Convert to properties useful for displaying in lists
   */
  toQuestionSearchItem(): QuestionSearchItem {
    return {
      // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
      fullDisplay: this.searchText,
      parentDisplay: this._surveyItem.isSurveyPageGroup
        ? 'Page'
        : this.pathToSearchText,
      itemDisplay: truncate(this.itemText, { length: 60 }),
      linkId: this.itemLinkId,
      searchParts: this.textParts,
      itemDescriptor: this.itemDescription,
      surveyId: this._surveyId,
      surveyItem: this._surveyItem,
    };
  }
}

/**
 * Assists in search survey items by their text
 */
export default class QuestionSearch {
  static filterOutItemType(
    questionSearchItems: QuestionSearchItem[],
    disallowedTypes: SurveyItemTypes[],
  ): QuestionSearchItem[] {
    return questionSearchItems.filter(
      _ => !(disallowedTypes || []).includes(_.surveyItem.type),
    );
  }

  private _surveyTree: SurveyTree;

  // lazy evaluate or this list will be generated on every survey update but used relatively rarely
  // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
  private _generatedSearchItems: SearchItem[] = null;

  constructor(surveyTree: SurveyTree) {
    this._surveyTree = surveyTree;
  }

  private get searchItems() {
    // only compute this once
    if (this._generatedSearchItems !== null) return this._generatedSearchItems;

    const rootItems = this._surveyTree.rootSurveyItems();

    const rootGroups = rootItems.map(rootItem => {
      const rootSearchItem = new SearchItem(rootItem);
      return rootSearchItem.getTargetAndChildSearchItems();
    });

    this._generatedSearchItems = flatten(rootGroups);
    return this._generatedSearchItems;
  }

  /**
   * Gets all the question search items of all input controls
   */
  get controlSearchList(): QuestionSearchItem[] {
    return this.searchItems
      .filter(searchItem => searchItem.isSearchTextForControl)
      .map(searchItem => searchItem.toQuestionSearchItem());
  }

  /**
   * Gets all question search items of survey builder question items.
   * Note that this can include display items and groups
   */
  get surveyBuilderQuestionSearchList(): QuestionSearchItem[] {
    return this.searchItems
      .filter(searchItem => searchItem.surveyItem.isSurveyBuilderQuestion)
      .map(searchItem => searchItem.toQuestionSearchItem());
  }

  /**
   * Pages that contain any input controls
   */
  get pagesWithControls(): QuestionSearchItem[] {
    return this.searchItems
      .filter(searchItem => searchItem.surveyItem.isSurveyPageGroup)
      .filter(
        searchItem =>
          !!searchItem
            .getTargetAndChildSearchItems()
            .find(searchItem => searchItem.surveyItem.isControl),
      )
      .map(searchItem => searchItem.toQuestionSearchItem());
  }

  /**
   * Pages that contain survey builder questions.  Note that this can include input questions or display question types
   */
  get pagesWithSurveyBuilderQuestions(): QuestionSearchItem[] {
    return this.searchItems
      .filter(searchItem => searchItem.surveyItem.isSurveyPageGroup)
      .filter(
        searchItem =>
          !!searchItem.surveyItem.subItems.find(
            subItem => subItem.isSurveyBuilderQuestion,
          ),
      )
      .map(searchItem => searchItem.toQuestionSearchItem());
  }

  /**
   * Gets the control items that come before (in pre-order index) the target link
   */
  getPriorControlSearchList(priorToLink: string): QuestionSearchItem[] {
    const targetIndex = this._surveyTree.getPreOrderIndexOfLink(priorToLink);
    return this.controlSearchList.filter(searchItem => {
      const searchItemIndex = this._surveyTree.getPreOrderIndexOfLink(
        searchItem.linkId,
      );
      return targetIndex > searchItemIndex;
    });
  }
}
